import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from '../hooks/useForm';
import { useResetPassword } from '../hooks/useResetPassword';
import '../auth.css';
import Header from '../header';

export default function ResetPassword() {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { resetPassword, loading, error } = useResetPassword();

  // Get token from URL parameters
  const tokenFromUrl = searchParams.get('token');

  const { values, handleChange, handleSubmit, setValues } = useForm({
    initialValues: {
      token: tokenFromUrl || '',
      newPassword: '',
      confirmPassword: '',
    },
    onSubmit: async formData => {
      if (formData.newPassword !== formData.confirmPassword) {
        return;
      }
      const result = await resetPassword(formData.token, formData.newPassword);
      if (result) {
        setSuccess(true);
        // Redirect to login page after successful password reset
        setTimeout(() => {
          navigate('/');
        }, 3000);
      }
    },
  });

  // Auto-populate token if present in URL
  useEffect(() => {
    if (tokenFromUrl) {
      setValues(prev => ({ ...prev, token: tokenFromUrl }));
    }
  }, [tokenFromUrl, setValues]);

  const passwordsMatch =
    values.newPassword === values.confirmPassword || !values.confirmPassword;

  return (
    <div className="bg-dark min-vh-100 d-flex flex-column">
      <Header />
      <div className="bg-dark min-vh-100 d-flex flex-column justify-content-center align-items-center text-white">
        <div className="col-md-5 form_card text-center">
          {/* Success Icon */}
          <div className="mb-4">
            <div
              className="mx-auto"
              style={{
                width: '80px',
                height: '80px',
                backgroundColor: '#28a745',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="40"
                height="40"
                fill="white"
                className="bi bi-shield-lock"
                viewBox="0 0 16 16"
              >
                <path d="M5.338 1.59a61.44 61.44 0 0 0-2.837.856.481.481 0 0 0-.328.39c-.554 4.157.726 7.19 2.253 9.188a10.725 10.725 0 0 0 2.287 2.233c.346.244.652.42.893.533.12.057.218.095.293.118a.55.55 0 0 0 .101.025.615.615 0 0 0 .1-.025c.076-.023.174-.061.294-.118.24-.113.547-.29.893-.533a10.726 10.726 0 0 0 2.287-2.233c1.527-1.997 2.807-5.031 2.253-9.188a.48.48 0 0 0-.328-.39c-.651-.213-1.75-.56-2.837-.855C9.552 1.29 8.531 1.067 8 1.067c-.53 0-1.552.223-2.662.524zM5.072.56C6.157.265 7.31 0 8 0s1.843.265 2.928.56c1.11.3 2.229.655 2.887.87a1.54 1.54 0 0 1 1.044 1.262c.596 4.477-.787 7.795-2.465 9.99a11.775 11.775 0 0 1-2.517 2.453 7.159 7.159 0 0 1-1.048.74c-.28.14-.56.25-.827.34l-.07.025A.655.655 0 0 1 8 15.5a.655.655 0 0 1-.224-.053l-.07-.025a7.158 7.158 0 0 1-.826-.34 7.159 7.159 0 0 1-1.048-.74 11.777 11.777 0 0 1-2.517-2.453C1.928 10.487.545 7.169 1.141 2.692A1.54 1.54 0 0 1 2.185 1.43 62.456 62.456 0 0 1 5.072.56z" />
                <path d="M9.5 6.5a1.5 1.5 0 0 1-1 1.415l.385 1.99a.5.5 0 0 1-.491.595h-.788a.5.5 0 0 1-.49-.595l.384-1.99a1.5 1.5 0 1 1 2-1.415z" />
              </svg>
            </div>
          </div>

          <h2 className="mb-4 fw-bold text-success">{t('reset_password.title')}</h2>

          {!success ? (
            <>
              {tokenFromUrl ? (
                <p className="text-center mb-4">
                  <i className="bi bi-check-circle text-success me-2"></i>
                  {t('reset_password.token_detected')}
                </p>
              ) : (
                <p className="text-center mb-4">
                  {t('reset_password.token_instruction')}
                </p>
              )}

              {error && <div className="alert alert-danger">{error}</div>}

              <form onSubmit={handleSubmit} className="text-start">
                <div className="mb-4">
                  <label className="form-label fw-semibold">
                    <i className="bi bi-key me-2 text-warning"></i>
                    {t('reset_password.reset_token')}
                  </label>
                  <input
                    type="text"
                    className={`form-control custom-input ${tokenFromUrl ? 'bg-light text-dark' : ''}`}
                    placeholder={
                      tokenFromUrl
                        ? t('reset_password.token_loaded')
                        : t('reset_password.token_paste')
                    }
                    name="token"
                    value={values.token}
                    onChange={handleChange}
                    required
                    readOnly={!!tokenFromUrl}
                    style={{ fontFamily: 'monospace' }}
                  />
                  <small className="d-block mt-2 text-muted">
                    <i className="bi bi-info-circle me-1"></i>
                    {t('reset_password.check_inbox')}
                  </small>
                </div>

                <div className="mb-4 position-relative">
                  <label className="form-label fw-semibold">
                    <i className="bi bi-lock me-2 text-warning"></i>
                    {t('reset_password.new_password')}
                  </label>
                  <div className="position-relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="form-control custom-input"
                      placeholder={t('reset_password.new_password_placeholder')}
                      name="newPassword"
                      value={values.newPassword}
                      onChange={handleChange}
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      className="btn btn-link position-absolute end-0 top-50 translate-middle-y pe-3 text-muted"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ border: 'none', background: 'none', zIndex: 10 }}
                    >
                      <i
                        className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}
                      ></i>
                    </button>
                  </div>
                  <small className="d-block mt-2 text-muted">
                    <i className="bi bi-shield-check me-1"></i>
                    {t('reset_password.min_chars')}
                  </small>
                </div>

                <div className="mb-4 position-relative">
                  <label className="form-label fw-semibold">
                    <i className="bi bi-lock-fill me-2 text-warning"></i>
                    {t('reset_password.confirm_password')}
                  </label>
                  <div className="position-relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      className={`form-control custom-input ${!passwordsMatch && values.confirmPassword ? 'is-invalid' : passwordsMatch && values.confirmPassword ? 'is-valid' : ''}`}
                      placeholder={t('reset_password.confirm_placeholder')}
                      name="confirmPassword"
                      value={values.confirmPassword}
                      onChange={handleChange}
                      required
                    />
                    <button
                      type="button"
                      className="btn btn-link position-absolute end-0 top-50 translate-middle-y pe-3 text-muted"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      style={{ border: 'none', background: 'none', zIndex: 10 }}
                    >
                      <i
                        className={`bi ${showConfirmPassword ? 'bi-eye-slash' : 'bi-eye'}`}
                      ></i>
                    </button>
                  </div>
                  {!passwordsMatch && values.confirmPassword && (
                    <small className="text-danger d-block mt-1">
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      {t('reset_password.passwords_no_match')}
                    </small>
                  )}
                  {passwordsMatch && values.confirmPassword && (
                    <small className="text-success d-block mt-1">
                      <i className="bi bi-check-circle me-1"></i>
                      {t('reset_password.passwords_match')}
                    </small>
                  )}
                </div>

                <button
                  type="submit"
                  className="btn btn-success w-100 py-3 fw-semibold"
                  disabled={loading || !passwordsMatch}
                  style={{
                    fontSize: '1.1rem',
                    borderRadius: '8px',
                    border: 'none',
                    background: passwordsMatch ? '#28a745' : '#6c757d',
                    transition: 'all 0.3s ease',
                  }}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      {t('reset_password.submitting')}
                    </>
                  ) : (
                    <>
                      <i className="bi bi-shield-check me-2"></i>
                      {t('reset_password.submit')}
                    </>
                  )}
                </button>
              </form>

              <div className="text-center mt-4 pt-3 border-top">
                <Link to="/" className="text-decoration-none text-warning">
                  <i className="bi bi-arrow-left me-1"></i>
                  {t('reset_password.back_login')}
                </Link>
              </div>
            </>
          ) : (
            <div className="text-center">
              <div className="mb-4">
                <div
                  className="mx-auto"
                  style={{
                    width: '100px',
                    height: '100px',
                    backgroundColor: '#28a745',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 0 20px rgba(40, 167, 69, 0.3)',
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="50"
                    height="50"
                    fill="white"
                    className="bi bi-check-circle-fill"
                    viewBox="0 0 16 16"
                  >
                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
                  </svg>
                </div>
              </div>

              <h3 className="text-success mb-3 fw-bold">
                <i className="bi bi-shield-check me-2"></i>
                {t('reset_password.success_title')}
              </h3>

              <div
                className="alert alert-success border-0"
                style={{ backgroundColor: 'rgba(40, 167, 69, 0.1)' }}
              >
                <div className="d-flex align-items-center">
                  <i className="bi bi-info-circle-fill text-success me-3 fs-5"></i>
                  <div className="text-start">
                    <strong>{t('reset_password.success_desc')}</strong>
                    <p className="mb-0 mt-1 text-muted">
                      {t('reset_password.redirecting')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <div className="d-flex align-items-center justify-content-center mb-3">
                  <span
                    className="spinner-border spinner-border-sm text-success me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  <span className="text-muted">{t('reset_password.redirecting')}</span>
                </div>

                <Link to="/" className="btn btn-outline-success">
                  <i className="bi bi-box-arrow-in-right me-2"></i>
                  {t('reset_password.go_login')}
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
